import { useEffect } from 'react';

import events from '~/modules/game-events/game-events';
import { useEventListenerSelector } from '~/modules/game-events/hooks';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import tuple from '~/modules/utils/tuple';
import InputSources from '~/routes/select-input/input-sources/index';
import { InputSourceList, InputSourceNames } from '~/routes/select-input/input-sources/interfaces';
import { RemoteMicrophoneInputSource } from '~/routes/select-input/input-sources/remote';
import { freePhoneForSlot } from '~/routes/select-input/remote-mic-slots';

/**
 * What a slot is currently pointed at, and the repair that keeps it pointed at something real.
 *
 * There is no "cycle to the next input" here any more: the setup screen offers every input at once
 * (see `input-options.ts`), so nothing needs to step through them. That also retires a bug of the
 * same family as the one `5f5ced9f2` fixed for Add - the old `cycleSource` walked `sources`, which
 * keeps `Dummy` when no real source has loaded, so cycling could land a slot on the absence of an
 * input and quietly empty it. A list that never offers `Dummy` cannot.
 */
export function usePlayerInput(playerNumber: PlayerNumber, sources: Record<string, InputSourceList>) {
  const sourceList = Object.keys(sources) as Array<InputSourceNames>;
  const [selectedPlayerInput, playerInputData, isPlayerInputInitialised] = useEventListenerSelector(
    [events.playerInputChanged, events.inputListChanged],
    () => {
      const playerInput = PlayersManager.getPlayer(playerNumber)?.input;

      if (!playerInput) return tuple([null, null, true]);
      return tuple([
        playerInput,
        InputSources.getInputForPlayerSelected(playerInput),
        InputSources.isPlayerInputInitialised(playerInput),
      ]);
    },
  );

  /**
   * Repairs a slot whose input cannot be resolved any more - the phone it was pointed at has gone,
   * or the device was unplugged - by putting it back on the first real source.
   *
   * `Dummy` is skipped, and doing nothing is the right answer when it is the only source left.
   * `useMicrophoneList` drops every source with an empty list, and the microphone list is empty
   * both before the permission is granted and for the moment `loadMics` spends re-reading it - so
   * the head of `sourceList` is `Dummy` more often than it looks, and assigning it here is not a
   * repair at all: it is what empties the slot the room just filled.
   */
  useEffect(() => {
    if (isPlayerInputInitialised && playerInputData === null) {
      const source = sourceList.find((name) => name !== 'Dummy');
      if (!source) return;
      // A phone already in another slot is no repair: it would sing for two slots and its own would be lost.
      const input =
        source === RemoteMicrophoneInputSource.inputName
          ? freePhoneForSlot(playerNumber, sources[source].list, PlayersManager.getPlayers())
          : sources[source].getDefault();
      if (input) {
        PlayersManager.getPlayer(playerNumber)?.changeInput(source, input.channel, input.deviceId);
      }
    }
  });

  return tuple([selectedPlayerInput, playerInputData]);
}
